import React, { useMemo } from 'react'
import { type CustomColumnDef } from './types'
import { useOptionalWarningHighlight } from './WarningHighlighterContext'
import { type GridRenderCellParams } from '@mui/x-data-grid'
import { Box } from '@mui/material'

/**
 * Enhances column definitions with optional cell ref registration
 * and warning highlight behavior when the WarningHighlight context
 * is available.
 */

export const useOptionalRefs = <T,>(columns: CustomColumnDef[]): CustomColumnDef[] => {
  const warningHighlight = useOptionalWarningHighlight()
  const registerCellRef = warningHighlight?.registerCellRef
  const highlightedCells = warningHighlight?.highlightedCells ?? {}

  return useMemo(() => {
    // No enhancements needed
    if (!registerCellRef) return columns

    return columns.map((column) => {
      if (!column.field) return column // Ignore actions column or invalid columns
      return {
        ...column,
        renderCell: (params: GridRenderCellParams) => {
          const key = `${params.id}:${column.field}`
          const isHighlighted = highlightedCells[key]
          return (
            <Box
              ref={(el: HTMLDivElement | null) => {
                registerCellRef(params.id as string, column.field, el)
              }}
              className={isHighlighted ? 'warning-highlight-cell' : ''}
              sx={{ minHeight: '50%', minWidth: '5%', display: 'flex', flexDirection: 'row', alignItems: 'inherit' }}
            >
              {column.renderCell ? column.renderCell(params) : params.value}
            </Box>
          )
        }
      }
    })
  }, [columns, registerCellRef, warningHighlight])
}
